import * as THREE from 'three'
import vehicles from '../../../content/vehicles.js'
import { signTexture, twoLineTexture } from '../../Utils/localizedTexture.js'
import { t, pick } from '../../../content/i18n.js'

/**
 * Garaj: üstü açık park cebi (tools/blender/garage.py). Arabayla cebe gir, Enter'a bas (ya da alana dokun)
 * → sıradaki araç; yerde her zaman "ENTER'A BAS / ARACI DEĞİŞTİR" yazar (çatılı hâlde anlaşılmıyordu).
 * Kısayol: 1…N rakam tuşları (üst sıra veya sayısal tuş takımı) doğrudan o aracı seçer.
 * Bina tools/blender/garage.py; araç listesi src/content/vehicles.js.
 */
export default class GarageSection
{
    constructor(_options)
    {
        // Options
        this.config = _options.config
        this.resources = _options.resources
        this.objects = _options.objects
        this.areas = _options.areas
        this.car = _options.car
        this.sounds = _options.sounds
        this.physics = _options.physics
        this.x = _options.x
        this.y = _options.y

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        this.listeners = new Set()

        this.setStatic()
        this.setLabel()
        this.setHint()
        this.setArea()
        this.setShortcuts()
    }

    setStatic()
    {
        this.objects.add({
            base: this.resources.items.garageBase.scene,
            collision: this.resources.items.garageCollision.scene,
            floorShadowTexture: this.resources.items.garageFloorShadowTexture,
            offset: new THREE.Vector3(this.x, this.y, 0),
            mass: 0
        })
    }

    currentName()
    {
        const vehicle = vehicles.find((_vehicle) => _vehicle.id === this.car.vehicle)
        const index = vehicles.indexOf(vehicle)
        return `${pick(vehicle.name)}  ${index + 1}/${vehicles.length}`
    }

    setLabel()
    {
        // Tabela yüzü (tools/blender/garage.py: cebin arkasında, ön yüz y = +2.75, z 1.575…2.825, genişlik 3.8):
        // üstte GARAJ / GARAGE, altta seçili araç — seçili dilde, şeffaf zeminli renkli kanvas
        this.label = {}
        this.label.texture = signTexture([
            { text: () => t('floorGarage'), color: '#5b4cff', size: 0.34, y: 0.33 },
            { text: () => this.currentName(), color: '#2b2f73', size: 0.24, y: 0.73, weight: 700 }
        ], 1024, 336)
        this.label.mesh = new THREE.Mesh(
            new THREE.PlaneGeometry(3.6, 3.6 * 336 / 1024),
            new THREE.MeshBasicMaterial({ map: this.label.texture, transparent: true, depthWrite: false, toneMapped: false })
        )
        this.label.mesh.rotation.x = Math.PI * 0.5 // -Y'ye (kameraya) bakar
        this.label.mesh.position.set(this.x, this.y + 2.744, 2.2)
        this.label.mesh.matrixAutoUpdate = false
        this.label.mesh.updateMatrix()
        this.container.add(this.label.mesh)
    }

    // Yerde, Enter alanının hemen önünde: ne yapılacağı her zaman görünsün
    setHint()
    {
        this.hint = {}
        this.hint.texture = twoLineTexture(() => t(this.config.touch ? 'garageHintTouch' : 'garageHintKey'), () => t('garageHintAction'))
        this.hint.mesh = new THREE.Mesh(
            new THREE.PlaneGeometry(4.6, 1.15),
            new THREE.MeshBasicMaterial({ transparent: true, alphaMap: this.hint.texture, color: 0xffffff, depthWrite: false })
        )
        this.hint.mesh.position.set(this.x, this.y - 3.05, 0.003)
        this.hint.mesh.matrixAutoUpdate = false
        this.hint.mesh.updateMatrix()
        this.container.add(this.hint.mesh)
    }

    setArea()
    {
        this.area = this.areas.add({
            position: new THREE.Vector2(this.x, this.y),
            halfExtents: new THREE.Vector2(1.9, 1.9)
        })

        this.area.on('interact', () =>
        {
            const index = vehicles.findIndex((_vehicle) => _vehicle.id === this.car.vehicle)
            this.select(vehicles[(index + 1) % vehicles.length].id)
        })
    }

    setShortcuts()
    {
        // Birincil: düz rakam (event.code: Digit1…, Numpad1…). Ctrl+rakam da denenir ama tarayıcılar
        // (Windows/Linux Chrome sekme geçişi, macOS Mission Control masaüstü geçişi) çoğu zaman sayfaya iletmez.
        window.addEventListener('keydown', (_event) =>
        {
            const match = /^(Digit|Numpad)([1-9])$/.exec(_event.code)
            if(!match || _event.altKey || _event.metaKey)
            {
                return
            }

            const vehicle = vehicles[Number(match[2]) - 1]
            if(!vehicle)
            {
                return
            }

            if(_event.ctrlKey)
            {
                _event.preventDefault()
            }

            if(vehicle.id !== this.car.vehicle)
            {
                this.select(vehicle.id)
            }
        })
    }

    select(_id)
    {
        this.car.setVehicle(_id)
        this.label.texture.userData.entry.render()
        this.physics.car.jump(false, 120)
        this.sounds.play('reveal')
        for(const _listener of this.listeners)
        {
            _listener(_id)
        }

        try
        {
            window.localStorage.setItem('llpause.vehicle', _id)
        }
        catch(_error) {}
    }
}
